import React from "react";
import { Box, Typography, Grid, Paper, Chip, Stack } from "@mui/material";
import { useLogger } from "../../../../contexts/LoggerContext";
import { useSchemes } from "../../../../contexts/SchemesContext";
import {
  OffensivePlayLog,
  OffensivePlaySelection,
} from "../../../../types/LogTypes";
import { jerseyNumberNameDisplay } from "../../../../helpers/helpers";
import { GameClockInput } from "../Components/GameClockInput";

export const OffenseDetails = () => {
  const { currentPlayCall, currentPlayLog, currentPlayPenalty } = useLogger();
  const {
    offensivePersonnel,
    formations,
    formationTags,
    passProtectionConcepts,
    passConcepts,
  } = useSchemes();

  const playCall = currentPlayCall as OffensivePlaySelection | null;
  const playLog = currentPlayLog as OffensivePlayLog | null;
  const Turnover = playLog ? playLog.fumble || playLog.interception : false;

  const personnel_group = playCall
    ? offensivePersonnel.find((p) => p.id === playCall.personnel_group_id)
    : null;
  const formation = playCall
    ? formations.find((f) => f.id === playCall.formation_id)
    : null;
  const formation_tags = playCall?.formation_tags
    ? playCall.formation_tags
        .map((t) => formationTags.find((ft) => ft.id === t)?.name)
        .filter(Boolean)
    : [];

  const passProtectionConcept = playCall?.pass_protection_concept_id
    ? passProtectionConcepts.find(
        (p) => p.id === playCall?.pass_protection_concept_id
      )
    : null;

  const passConcept = playCall?.pass_concept_id
    ? passConcepts.find((p) => p.id === playCall?.pass_concept_id)
    : null;

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={3} direction="row" alignItems="stretch">
        {/* Play Call Section */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: "background.paper",
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Play Call
            </Typography>
            <Stack spacing={2}>
              {/* Labels Row */}
              <Grid container>
                <Grid item xs={3}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    align="center"
                  >
                    Formation
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    align="center"
                  >
                    Personnel
                  </Typography>
                </Grid>
                {playCall?.run_code_word && (
                  <Grid item xs={3}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      align="center"
                    >
                      Run Code Word
                    </Typography>
                  </Grid>
                )}
                {passProtectionConcept && (
                  <Grid item xs={3}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      align="center"
                    >
                      Pass Protection
                    </Typography>
                  </Grid>
                )}
                {passConcept && (
                  <Grid item xs={3}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      align="center"
                    >
                      Route Concept
                    </Typography>
                  </Grid>
                )}
              </Grid>
              {/* Values Row */}
              <Grid container alignItems="center">
                <Grid item xs={3}>
                  <Typography variant="h6" align="center">
                    {formation?.name || ""} {playCall?.direction || ""}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="h6" align="center">
                    {personnel_group?.name || ""}
                  </Typography>
                </Grid>
                {playCall?.run_code_word && (
                  <Grid item xs={3}>
                    <Typography variant="h6" align="center">
                      {playCall?.run_code_word || ""}
                    </Typography>
                  </Grid>
                )}
                {passProtectionConcept && (
                  <Grid item xs={3}>
                    <Typography variant="h6" align="center">
                      {passProtectionConcept.name || ""}
                    </Typography>
                  </Grid>
                )}
                {passConcept && (
                  <Grid item xs={3}>
                    <Typography variant="h6" align="center">
                      {passConcept.name || ""}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              {formation_tags.length > 0 && (
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="space-between"
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ minWidth: 120 }}
                  >
                    Formation Tags
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {formation_tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Play Result Section */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: "background.paper",
              borderRadius: 2,
              position: "relative",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Play Result
            </Typography>
            <Stack spacing={2}>
              {/* Labels Row */}
              <Grid container>
                <Grid item xs={4}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    align="center"
                  >
                    Gain/Loss
                  </Typography>
                </Grid>
                {playLog?.passer_roster_player && (
                  <Grid item xs={4}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      align="center"
                    >
                      Passer
                    </Typography>
                  </Grid>
                )}
                {playLog?.receiver_roster_player && (
                  <Grid item xs={4}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      align="center"
                    >
                      Receiver
                    </Typography>
                  </Grid>
                )}
                {playLog?.rusher_roster_player && (
                  <Grid item xs={4}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      align="center"
                    >
                      Rusher
                    </Typography>
                  </Grid>
                )}
              </Grid>
              {/* Values Row */}
              <Grid container alignItems="center">
                <Grid item xs={4}>
                  <Typography
                    variant="h5"
                    color={
                      playLog && playLog.gain_loss >= 0
                        ? "success.main"
                        : "error.main"
                    }
                    align="center"
                  >
                    {playLog ? (
                      <>
                        {playLog.gain_loss > 0 ? "+" : ""}
                        {playLog.gain_loss} yards
                      </>
                    ) : (
                      "0 yards"
                    )}
                  </Typography>
                </Grid>
                {playLog?.passer_roster_player && (
                  <Grid item xs={4} sx={{ display: "flex", justifyContent: "center" }}>
                    <Chip
                      label={
                        playLog?.passer_roster_player
                          ? jerseyNumberNameDisplay(
                              playLog.passer_roster_player
                            )
                          : ""
                      }
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "text.primary",
                        backgroundColor: "background.paper",
                        borderColor: "divider",
                      }}
                    />
                  </Grid>
                )}
                {playLog?.receiver_roster_player && (
                  <Grid item xs={4} sx={{ display: "flex", justifyContent: "center" }}>
                    <Chip
                      label={
                        playLog?.receiver_roster_player
                          ? jerseyNumberNameDisplay(
                              playLog.receiver_roster_player
                            )
                          : ""
                      }
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "text.primary",
                        backgroundColor: "background.paper",
                        borderColor: "divider",
                        justifyContent: "center",
                      }}
                    />
                  </Grid>
                )}
                {playLog?.rusher_roster_player && (
                  <Grid item xs={4} sx={{ display: "flex", justifyContent: "center" }}>
                    <Chip
                      label={
                        playLog?.rusher_roster_player
                          ? jerseyNumberNameDisplay(
                              playLog.rusher_roster_player
                            )
                          : ""
                      }
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "text.primary",
                        backgroundColor: "background.paper",
                        borderColor: "divider",
                        justifyContent: "center",
                      }}
                    />
                  </Grid>
                )}
              </Grid>
              {/* Turnover Warning */}
              {Turnover && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    backgroundColor: "error.main",
                    color: "error.contrastText",
                    borderRadius: 1,
                    textAlign: "center",
                  }}
                >
                  {playLog?.fumble && (
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      FUMBLE
                    </Typography>
                  )}
                  {playLog?.interception && (
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      INTERCEPTION
                    </Typography>
                  )}
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Penalties Section - Only shown if there are penalties */}
        {currentPlayPenalty && (
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                height: "100%",
                backgroundColor: "background.paper",
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Penalty
              </Typography>
              <Stack spacing={2}>
                {/* Labels Row */}
                <Grid container>
                  <Grid item xs={2}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      align="center"
                    >
                      Club
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      align="center"
                    >
                      Penalty Name
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      align="center"
                    >
                      Yards
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      align="center"
                    >
                      Accepted
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      align="center"
                    >
                      Player
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      align="center"
                    >
                      {/* Empty for spacing */}
                    </Typography>
                  </Grid>
                </Grid>
                {/* Values Row */}
                <Grid container alignItems="center">
                  <Grid
                    item
                    xs={2}
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    {currentPlayPenalty.club?.profile_picture ? (
                      <Box
                        component="img"
                        src={currentPlayPenalty.club.profile_picture}
                        alt={currentPlayPenalty.club.name || "Club"}
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: 1,
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <Typography variant="body1">N/A</Typography>
                    )}
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="h6" align="center">
                      {currentPlayPenalty.penalty.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body1" align="center">
                      {currentPlayPenalty.penalty.yards}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body1" align="center">
                      {currentPlayPenalty.accepted ? "Yes" : "No"}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body1" align="center">
                      {currentPlayPenalty.roster_player
                        ? jerseyNumberNameDisplay(
                            currentPlayPenalty.roster_player
                          )
                        : "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    {/* Empty for spacing */}
                  </Grid>
                </Grid>
              </Stack>
            </Paper>
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: "background.paper",
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              minWidth: 320,
            }}
          >
            <GameClockInput />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
